// src/utils/api.js

// Импортируем SERVER_BASE_URL из config.js (или .env, если используете REACT_APP_API_URL)
import { SERVER_BASE_URL } from "../config";

// Универсальная функция
export async function fetchEvents(token) {
  const response = await fetch(`${SERVER_BASE_URL}/api/events/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
}

export async function fetchUserDefaults(token) {
  const response = await fetch(`${SERVER_BASE_URL}/api/users/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
}

export async function createEvent(eventData, token) {
  try {
    // Проверяем и корректируем данные перед отправкой
    const dataToSend = { ...eventData };
    
    // 1. Проверка start_time и установка значения по умолчанию, если оно отсутствует
    if (!dataToSend.start_time) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(20, 0, 0, 0);
      dataToSend.start_time = tomorrow.toISOString();
      console.log("Using default start_time:", dataToSend.start_time);
    }
    
    // 2. Преобразование event_duration в число
    if (dataToSend.event_duration) {
      dataToSend.event_duration = parseFloat(dataToSend.event_duration);
    }
    
    // 3. Вычисление end_time на основе start_time и event_duration, если нужно
    if (dataToSend.start_time && dataToSend.event_duration && !dataToSend.end_time) {
      const startTime = new Date(dataToSend.start_time);
      const durationHours = parseFloat(dataToSend.event_duration || 24);
      const endTime = new Date(startTime.getTime() + (durationHours * 60 * 60 * 1000));
      dataToSend.end_time = endTime.toISOString();
    }
    
    console.log("Sending event data:", dataToSend);
    
    // Отправка запроса с проверенными данными
    const response = await fetch(`${SERVER_BASE_URL}/api/events/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(dataToSend),
    });
    
    // Логирование для отладки
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Create event error:", response.status, errorText);
    }
    
    return response;
  } catch (error) {
    console.error("Error creating event:", error);
    // Возвращаем объект для совместимости с вызывающим кодом
    return {
      ok: false,
      status: 500,
      json: () => Promise.resolve({ detail: error.message }),
    };
  }
}

export async function updateEvent(eventId, eventData, token) {
  const response = await fetch(`${SERVER_BASE_URL}/api/events/${eventId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(eventData),
  });
  return response;
}

export async function deleteEvent(eventId, token) {
  const response = await fetch(`${SERVER_BASE_URL}/api/events/${eventId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
}

// ===================== Admin =====================
export async function fetchUserStats(token) {
  return fetch(`${SERVER_BASE_URL}/api/admin/users/stats`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function fetchUsersList({ 
  token, 
  skip = 0, 
  limit = 100, 
  subscriptionType, 
  role, 
  subscriptionStatus, 
  search, 
  dateRange 
}) {
  const params = new URLSearchParams({ skip, limit });
  if (subscriptionType) params.append("subscription_type", subscriptionType);
  if (role) params.append("role", role);
  if (subscriptionStatus) params.append("subscription_status", subscriptionStatus);
  if (search) params.append("search", search);
  if (dateRange) params.append("date_range", dateRange);

  return fetch(`${SERVER_BASE_URL}/api/admin/users?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

// Обновление данных пользователя
export async function updateUser(token, userData) {
  return fetch(`${SERVER_BASE_URL}/api/users/${userData.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(userData),
  });
}

// Удаление пользователя
export async function deleteUser(token, userId) {
  return fetch(`${SERVER_BASE_URL}/api/users/${userId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function fetchEventStats(token) {
  return fetch(`${SERVER_BASE_URL}/api/admin/events/stats`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

// ===================== Event Status Utils =====================

/**
 * Универсальная функция для определения статуса события
 * @param {Object} event - Объект события с полями start_time, end_time, event_duration
 * @param {number} defaultDuration - Длительность по умолчанию в часах, если нет event_duration
 * @returns {string} - Статус события: "upcoming", "ongoing", "past" или "unknown"
 */
export const getEventStatus = (event, defaultDuration = 24) => {
  if (!event?.start_time) return "unknown";
  
  const now = new Date();
  const start = new Date(event.start_time);
  
  // Определение конечного времени
  let end;
  if (event.end_time) {
    end = new Date(event.end_time);
  } else {
    // Используем event_duration если есть, иначе defaultDuration
    const duration = event.event_duration || defaultDuration;
    end = new Date(start.getTime() + (parseFloat(duration) * 60 * 60 * 1000));
  }
  
  // Определение статуса
  if (now < start) return "upcoming";
  if (now >= start && now <= end) return "ongoing";
  return "past";
};

/**
 * Функция для получения цвета статуса для отображения
 * @param {string} status - Статус события
 * @returns {string} - CSS-класс для цвета
 */
export const getStatusColorClass = (status) => {
  switch (status) {
    case "upcoming": return "bg-green-100 text-green-800";
    case "ongoing": return "bg-indigo-100 text-indigo-800";
    case "past": return "bg-gray-100 text-gray-800";
    default: return "bg-gray-100 text-gray-800";
  }
};